import {BehaviorSubject} from 'rxjs';

import ENV from 'web-app/config/environment';
import {Headers} from 'web-app/api/clients/constants';
import {getBaseUrl} from 'web-app/util/location';
import generateUUID from 'web-app/util/uuid';
import {getAccessToken} from 'web-app/api/clients/famly-app';
import getInstallationId from 'web-app/services/installation-id-manager';
import {ACCESS_TOKEN_KEY} from 'web-app/util/access-token-helpers';
import {findRouteTemplateFromUncleanUrl} from 'web-app/react/routes/helpers';
import {hasValue} from 'web-app/util/typescript';

import LocalStorageService from './local-storage';

class APIHelper {
    apiKeySubject = new BehaviorSubject(this.apiKey);

    subscribeToApiKey(cb) {
        return this.apiKeySubject.subscribe(apiKey => cb(apiKey));
    }

    initConfig(key) {
        this.savedAPIKey = key;
        this.apiKeySubject.next(key || null);
    }

    getConfigByKey(key) {
        const config = ENV.APP.apiConfigs.find(conf => conf.key === key);
        if (config) {
            return config;
        }
        throw new Error(`getConfigByKey - API config not found: ${key}`);
    }

    getApiUrlByKey(key) {
        return this.getConfigByKey(key).api;
    }

    getGraphQLUrlByKey(key) {
        return this.getConfigByKey(key).graphql;
    }

    constructor() {
        this.initConfig(LocalStorageService.getFromLocalStorage('apiKey'));
    }

    getRequestHeaders(authenticated) {
        const headers = {};
        const installationId = getInstallationId();

        if (authenticated) {
            headers[Headers.XFamlyAccesstoken] = getAccessToken();
        }

        // Request identifier that the backend returns in the response, allowing the frontend to verify that the request
        // matches the response
        const requestUUID = generateUUID();

        headers[Headers.XFamlyPlatform] = ENV.APP.platform;
        headers[Headers.XFamlyVersion] = ENV.APP.version;
        headers[Headers.XFamlyRequestUuid] = requestUUID;

        const routeTemplate = findRouteTemplateFromUncleanUrl(window.location.hash);

        if (hasValue(routeTemplate)) {
            headers[Headers.XFamlyRoute] = routeTemplate;
        }

        if (installationId) {
            headers[Headers.XFamlyInstallationId] = installationId;
        }

        return {
            headers,
            requestUUID,
        };
    }

    get baseConfig() {
        return ENV.APP.apiConfigs.find(conf => conf.isBase);
    }

    get loginsAPIKey() {
        if (this.savedAPIKey || ENV.APP.apiConfigs.length === 1) {
            return this.apiKey;
        }

        const config = this.baseConfig;
        if (config) {
            return config.key;
        }
        throw new Error(`loginsAPIKey config not found`);
    }

    get apiKeys() {
        return ENV.APP.apiConfigs.map(conf => conf.key);
    }

    get hasAPIKey() {
        return Boolean(this.savedAPIKey);
    }

    get apiConfig() {
        if (this.savedAPIKey && ENV.APP.apiConfigs.length > 1) {
            return this.getConfigByKey(this.savedAPIKey);
        }
        // If there is an accessToken but no savedAPIKey
        // return the baseConfig since they can't be logged in to a new tld
        else if (LocalStorageService.getFromLocalStorage(ACCESS_TOKEN_KEY)) {
            return this.baseConfig;
        } else {
            return ENV.APP.apiConfigs[0];
        }
    }

    setApiConfig(key) {
        if (this.apiKeys.includes(key)) {
            LocalStorageService.saveToLocalStorage('apiKey', key);
            this.initConfig(key);
        } else {
            throw new Error(`Set apiConfig - API config not found: ${key}`);
        }
    }

    clearAPIConfig() {
        LocalStorageService.clearKey('apiKey');
        this.initConfig();
    }

    get apiKey() {
        return this.apiConfig.key;
    }

    get signinUrlDev() {
        return convertToSigninUrl(getBaseUrl());
    }

    get signinUrlV1() {
        const environmentUrl = this.environmentUrl;
        if (environmentUrl.startsWith('/')) {
            return this.signinUrlDev;
        }
        return convertToSigninUrl(environmentUrl);
    }

    get signinUrlV2() {
        if (window.location.host === 'app.famly.local') {
            return this.signinUrlDev;
        }

        // Adding the /v2 path to the "old" signin url (e.g. "signin.famly.co/v2") will redirect to the new signin app
        // and store that information in a cookie such that future visits to signin.famly.co will keep redirecting to
        // the new signin app.
        return `${this.signinUrlV1}/v2`;
    }

    get environmentUrl() {
        return this.apiConfig.app;
    }

    get baseUrl() {
        return getBaseUrl();
    }

    get apiUrl() {
        return this.apiConfig.api;
    }

    get famlyapiUrl() {
        return this.apiConfig.famlyapi;
    }

    get graphqlUrl() {
        return this.apiConfig.graphql;
    }
}

const convertToSigninUrl = url => {
    return url.replace('app.famly', 'signin.famly').replace('app.tryfamly', 'signin.tryfamly');
};

const apiHelper = new APIHelper();
export default apiHelper;
